import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { specLabelKey } from '../../domain/cars/carProperties';
import { cardOf } from '../../domain/game/overtakeDeck';
import { matchOutcome } from '../../domain/game/overtakeScoring';

/**
 * What the round was worth, and what the match came to (SPEC-011 PR-11.3).
 *
 * Two endings in one component because they are the same shape — a verdict, a
 * number, and the one control that carries the reader onward — and because the
 * round result is the thing that becomes the match result on the last round.
 * Splitting them would mean two stylesheets for one band that never appears
 * twice.
 *
 * The forfeit gets its own line rather than being folded into the pot. A reader
 * who doubled and lost has had a card taken out of their hand, and a card
 * leaving a hand for a reason other than being played is the one event in this
 * game that would otherwise be invisible — they would simply find it missing.
 */
export default function OvertakeRound({
    match, deck, mySide = 'player', onNext, onAgain, onLeave,
}) {
    const t = useTranslate();
    const isOver = match.status === 'over';
    const round = match.lastRound;
    const outcome = matchOutcome(match, deck);

    if (isOver) {
        return (
            <section className="overtake-result overtake-result--final" aria-live="polite">
                <p className="overtake-result__verdict">
                    {t(outcome.winner === null
                        ? 'play.over.draw'
                        : `play.over.${outcome.winner === mySide ? 'player' : 'opponent'}`)}
                </p>
                <p className="overtake-result__totals figure">
                    {t('play.over.totals', {
                        you: outcome.totals[mySide],
                        rival: outcome.totals[mySide === 'player' ? 'opponent' : 'player'],
                    })}
                </p>
                <div className="overtake-result__actions">
                    <button type="button" className="button button--primary" onClick={onAgain}>
                        {t('play.again')}
                    </button>
                    <button type="button" className="button button--quiet" onClick={onLeave}>
                        {t('play.leave')}
                    </button>
                </div>
            </section>
        );
    }

    if (!round) {
        return null;
    }

    return (
        <section className="overtake-result" aria-live="polite">
            <p className="overtake-result__verdict">
                {t(round.winner === mySide ? 'play.round.won' : 'play.round.lost')}
            </p>
            <p className="overtake-result__pot">
                {t('play.round.pot', {
                    count: round.value,
                    figure: t(specLabelKey(round.spec)),
                })}
            </p>

            {round.forfeit ? (
                <p className="overtake-result__forfeit">
                    {t('play.round.forfeit', { car: carName(cardOf(deck, round.forfeit)) })}
                </p>
            ) : null}

            <button type="button" className="button button--primary" onClick={onNext}>
                {t('play.next')}
            </button>
        </section>
    );
}
